import React, { useEffect, useRef, useState } from "react";
import { Loader2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import api, { formatApiErrorDetail } from "../lib/api";

export const AdminSmsAddon = ({ tenantId }) => {
    const [settings, setSettings] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [retry, setRetry] = useState(0);
    const saving = useRef(false);
    useEffect(() => {
        let cancelled = false;
        setSettings(null);
        setError("");
        api.get(`/admin/tenants/${tenantId}/sms-addon`).then(({ data }) => {
            if (!cancelled) setSettings(data);
        }).catch(() => { if (!cancelled) setError("Could not load SMS add-on access."); });
        return () => { cancelled = true; };
    }, [tenantId, retry]);

    const changeAccess = async (enabled) => {
        if (!settings || saving.current) return;
        saving.current = true;
        setBusy(true);
        setError("");
        try {
            const { data } = await api.patch(`/admin/tenants/${tenantId}/sms-addon`, { enabled });
            setSettings(data);
            toast.success(enabled ? "SMS add-on enabled — awaiting client opt-in" : "SMS add-on disabled");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not update SMS add-on access.");
        } finally {
            saving.current = false;
            setBusy(false);
        }
    };

    return (
        <section className="border-y border-zinc-800 py-4 space-y-3" data-testid={`client-sms-addon-${tenantId}`}>
            <div className="flex items-center justify-between gap-4">
                <label htmlFor={`sms-addon-${tenantId}`} className="flex items-center gap-2 text-[13px] font-medium text-zinc-200">
                    <MessageSquareText className="h-4 w-4 shrink-0" /> Paid SMS add-on
                </label>
                {settings ? <Switch id={`sms-addon-${tenantId}`} checked={settings.addon_enabled}
                    disabled={busy} onCheckedChange={changeAccess} aria-busy={busy}
                    data-testid={`client-sms-addon-toggle-${tenantId}`} aria-label="Paid SMS add-on access" />
                    : <Loader2 data-testid={`client-sms-addon-loading-${tenantId}`} aria-label="Loading SMS access"
                        className="h-4 w-4 animate-spin text-zinc-500" />}
            </div>
            <p className="text-[12px] text-zinc-400" data-testid={`client-sms-addon-status-${tenantId}`}>
                {busy ? "Saving access…" : !settings ? "Loading…" : !settings.addon_enabled ? "Not included" : settings.opted_in ? "Active · client opted in" : "Enabled · awaiting client opt-in"}
                {busy && <Loader2 className="ml-2 inline h-3 w-3 animate-spin" />}
            </p>
            <p className="text-[12px] text-zinc-500" data-testid={`client-sms-addon-billing-note-${tenantId}`}>
                Payment handled separately. Enabling access does not charge the client or send a text.
            </p>
            {error && <div role="alert" data-testid={`client-sms-addon-error-${tenantId}`} className="text-[12px] text-red-300">
                {error}{" "}<button type="button" onClick={() => setRetry((n) => n + 1)}
                    data-testid={`client-sms-addon-retry-${tenantId}`} className="underline hover:text-red-100 transition-colors">Retry</button>
            </div>}
        </section>
    );
};